import { invalidate } from "@react-three/fiber";
import * as THREE from "three";
import { getAddShapeBlockedReason } from "../../../store/addShapeBlockedReason";
import { editorStore } from "../../../store/editorStore";
import { floorplannerStore } from "../../../store/floorplannerStore";
import { CatalogImageOwner } from "../../../features/floorplan3d/catalog/catalogQueries";
import {
  getCatalogCategorySnapshots,
  getCatalogItemDisplayName,
} from "../../../features/floorplan3d/catalog/catalogDisplay";
import { normalizeCatalogProductDimensions } from "../../../features/floorplan3d/products/productDimensionUtils";
import { omitProduct3DFootprintSvg } from "../../../features/floorplan3d/products/product3DAssetHydration";
import { Product3DAssetReference } from "../../../features/floorplan3d/products/product3DTypes";
import { projectPointOntoArc } from "../../../utils/arcUtils";
import { getArchitectureSymbolSvgPath } from "../SymbolLibraryTabs";

const readyAsset = (product: CatalogImageOwner) => {
  const asset = product.active3DAsset;
  return asset?.status === "ready" && asset.glbUrl ? asset : undefined;
};

const escapeXmlText = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&apos;",
      })[character] ?? character,
  );

export const architectureSymbolSvgResolver = {
  resolve: getArchitectureSymbolSvgPath,
};

/** Encode a catalog footprint as a data URL (or pass through library URLs). */
const footprintDataUrl = (
  product: CatalogImageOwner,
  asset: Product3DAssetReference,
) => {
  const architectureSvg = asset.architectureSymbolKey
    ? architectureSymbolSvgResolver.resolve(asset.architectureSymbolKey)
    : undefined;
  const svgPath = architectureSvg || asset.footprintSvg;
  if (svgPath) {
    return svgPath.startsWith("<") || svgPath.includes("<svg")
      ? `data:image/svg+xml;utf8,${encodeURIComponent(svgPath)}`
      : svgPath;
  }
  const label = escapeXmlText(
    getCatalogItemDisplayName(product).slice(0, 2).toUpperCase(),
  );
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 70"><rect x="3" y="3" width="94" height="64" rx="5" fill="#fff" stroke="#171717" stroke-width="3"/><text x="50" y="43" text-anchor="middle" font-family="Arial,sans-serif" font-size="20" font-weight="700" fill="#171717">${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const closestPointOnSegment = (
  point: THREE.Vector2,
  start: THREE.Vector2,
  end: THREE.Vector2,
) => {
  const segment = end.clone().sub(start);
  const lengthSq = segment.lengthSq();
  if (lengthSq <= 1e-8) return start.clone();
  const t = Math.max(0, Math.min(1, point.clone().sub(start).dot(segment) / lengthSq));
  return start.clone().add(segment.multiplyScalar(t));
};

const findNearestWall = (point: THREE.Vector2) => {
  let best:
    | { wallId: string; point: THREE.Vector2; distanceSq: number }
    | undefined;
  floorplannerStore.wallsMap.forEach((wall, wallId) => {
    const candidate =
      wall.lineForm === "arc" && wall.controlPoint
        ? projectPointOntoArc(point, wall.start, wall.controlPoint, wall.end).point
        : closestPointOnSegment(point, wall.start, wall.end);
    const distanceSq = candidate.distanceToSquared(point);
    if (!best || distanceSq < best.distanceSq) {
      best = { wallId, point: candidate, distanceSq };
    }
  });
  return best;
};

const selectInsertedSymbol = (id: string) => {
  const symbol = floorplannerStore.symbolsMap.get(id);
  if (!symbol) return;
  editorStore.clearSelections();
  floorplannerStore.updateSymbolProperty(id, "selected", true);
  editorStore.addSelection(symbol);
};

export const insertPublishedProduct3D = (
  product: CatalogImageOwner,
): { ok: true; id: string } | { ok: false; reason: string } => {
  const asset = readyAsset(product);
  if (!asset) {
    return { ok: false, reason: "This product no longer has a published 3D object." };
  }
  if (floorplannerStore.lockedByOther) {
    return { ok: false, reason: getAddShapeBlockedReason(floorplannerStore) };
  }
  const placement = asset.placement ?? { mode: "floor", elevation: 0 };
  const [centerX, centerY] = editorStore.getVisibleCenter();
  const center = new THREE.Vector2(centerX, centerY);
  const nearestWall = placement.mode === "wall" ? findNearestWall(center) : undefined;
  if (placement.mode === "wall" && !nearestWall) {
    return {
      ok: false,
      reason: "Add a wall before placing this wall-mounted product, then try again.",
    };
  }

  const normalized = normalizeCatalogProductDimensions(product);
  const dimensions = {
    width: asset.dimensions?.width ?? normalized.dimensions.width ?? 1,
    depth: asset.dimensions?.depth ?? normalized.dimensions.depth ?? 1,
    height: asset.dimensions?.height ?? normalized.dimensions.height ?? 1,
  };
  const displayName = getCatalogItemDisplayName(product);
  const imageUrl = product.images?.[0] ?? undefined;
  const svgPath = footprintDataUrl(product, asset);
  const insertionPoint = nearestWall?.point ?? center;
  const quarterTurn = Math.PI / 2;
  const id = floorplannerStore.addSymbol("svg", insertionPoint.toArray(), {
    name: displayName,
    svgPath,
    svgLength: dimensions.width,
    svgHeight: dimensions.depth,
    ratio: dimensions.width / Math.max(dimensions.depth, 0.001),
    lockAspectRatio: true,
    libraryKey: "PRODUCT_3D",
    lineColor: 0x171717,
    threeD: {
      sourceMode: "custom",
      modelKey: "living:coffee-table-oval",
      dimensions,
      height: dimensions.height,
      baseElevation: placement.elevation,
      placementMode: placement.mode,
      collision: "box",
      modelPitchOffset: (placement.rotationXQuarterTurns ?? 0) * quarterTurn,
      modelRollOffset: (placement.rotationYQuarterTurns ?? 0) * quarterTurn,
      modelYawOffset: (placement.rotationZQuarterTurns ?? 0) * quarterTurn,
      product: {
        productId: product.id,
        renderMode: "asset3D",
        selectedImageUrl: imageUrl,
        // Keep footprintSvg off the symbol: svgPath already holds the 2D silhouette.
        asset: omitProduct3DFootprintSvg(asset),
        name: displayName,
        brand: product.brand ?? undefined,
        supplierName: product.supplier?.name ?? undefined,
        dimensions,
        dimensionConfidence: "exact",
        dimensionUnit: "m",
        dimensionSource: "publishedProduct3DAsset",
      },
    },
  });
  if (!id) {
    return { ok: false, reason: getAddShapeBlockedReason(floorplannerStore) };
  }

  floorplannerStore.beginUndoTransaction("Insert catalog 3D product");
  if (nearestWall) {
    floorplannerStore.recordWallForUndo(nearestWall.wallId);
    floorplannerStore.attachSymbolToWall(id, nearestWall.wallId, nearestWall.point.toArray());
    const symbol = floorplannerStore.symbolsMap.get(id);
    if (symbol?.threeD) {
      floorplannerStore.updateSymbolProperty(id, "threeD", {
        ...symbol.threeD,
        wallMount: {
          wallId: nearestWall.wallId,
          side: "left",
          offset: 0,
          distanceFromWall: 0,
        },
      });
    }
    floorplannerStore.recordWallChange(nearestWall.wallId, "modify");
  }
  floorplannerStore.recordSymbolChange(id, "add");
  floorplannerStore.upsertCatalogDictionaryEntry("product", product.id, {
    id: product.id,
    name: displayName,
    description: product.description ?? undefined,
    imageUrl,
    brand: product.brand ?? undefined,
    supplierName: product.supplier?.name ?? undefined,
    price:
      typeof product.price === "number" && Number.isFinite(product.price)
        ? product.price
        : undefined,
    categories: getCatalogCategorySnapshots(product),
    colors: product.colors
      ?.filter(Boolean)
      .map((color) => ({
        id: color?.id ?? undefined,
        name: color?.name ?? undefined,
        hex: color?.hex ?? undefined,
      })),
  });
  floorplannerStore.pushToUndoStack();
  selectInsertedSymbol(id);
  invalidate();
  return { ok: true, id };
};
